import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";

const PageShell = ({ eyebrow, headline, updated, children, testid }) => (
  <section className="min-h-screen" data-testid={testid}>
    <div className="container-riq pt-16 pb-24 max-w-3xl">
      <p className="eyebrow mb-4">{eyebrow}</p>
      <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.05]">
        <MaskedLine delay={0.1}>{headline}</MaskedLine>
      </h1>
      {updated && <p className="mt-4 font-nav text-xs uppercase tracking-[0.14em] text-[#4A5A63]">Last updated: {updated}</p>}
      <div className="mt-10 space-y-8 text-sm leading-relaxed text-[#2A3540]">{children}</div>
    </div>
  </section>
);

const Block = ({ title, children }) => (
  <div>
    <h2 className="font-display font-bold text-xl tracking-tight mb-3 text-[#101820]">{title}</h2>
    <div className="text-[#4A5A63] space-y-3">{children}</div>
  </div>
);

const Sub = ({ title, children }) => (
  <div>
    <h3 className="font-display font-semibold text-base tracking-tight mb-1.5 text-[#101820]">{title}</h3>
    <div className="text-[#4A5A63] space-y-2">{children}</div>
  </div>
);

const List = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5">
    {items.map((i) => <li key={i}>{i}</li>)}
  </ul>
);

export const Privacy = () => (
  <PageShell eyebrow="Legal · Privacy" headline={<>Privacy <span className="accent-word">Policy.</span></>} updated="March 27, 2026" testid="legal-privacy">
    <p className="text-[#4A5A63]">
      RresponsIQ ("we," "our," or "us"), a Xyle company, is committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website responsiq.com and use our ad-tech platform and services (collectively, the "Services").
    </p>
    <Block title="1. Information We Collect">
      <p>We may collect information about you in various ways:</p>
      <Sub title="a. Personal Data">
        <p>When you request a demo, contact us, or use certain features of our Services, we may collect personally identifiable information, such as your name, work email address, company name, role, and media budget range.</p>
      </Sub>
      <Sub title="b. Usage Data">
        <p>We automatically collect certain information when you access or use our Services, including your IP address, browser type, operating system, referral URLs, pages viewed, time spent on pages, and other usage statistics. For our ad-tech platform, this may include ad impressions, clicks, conversions, and device identifiers.</p>
      </Sub>
      <Sub title="c. Cookies and Tracking Technologies">
        <p>We use cookies and similar tracking technologies (like pixels and web beacons) to track activity on our Services and hold certain information. These are used for purposes such as remembering your preferences, analyzing traffic, and measuring conversions. You can control cookie preferences through the consent banner on this site or your browser settings.</p>
      </Sub>
    </Block>
    <Block title="2. How We Use Your Information">
      <List items={[
        "Provide, operate, and maintain our Services.",
        "Respond to demo requests and manage your account.",
        "Improve, personalize, and expand our Services.",
        "Understand and analyze how you use our Services.",
        "Develop new products, services, features, and functionality.",
        "Communicate with you, including for customer service, updates, and marketing.",
        "Detect and prevent fraud and other illegal activities.",
        "Comply with legal obligations.",
        "Deliver relevant advertisements and measure their effectiveness.",
      ]} />
    </Block>
    <Block title="3. How We Share Your Information">
      <List items={[
        "Service Providers: third-party vendors, consultants, and other service providers who perform services on our behalf, such as data analysis, email delivery, hosting services, and customer service.",
        "Business Transfers: in connection with any merger, sale of company assets, financing, or acquisition of all or a portion of our business by another company.",
        "Affiliates: we may share your information with our affiliates, including Xyle and MonetiseUP, in which case we will require those affiliates to honor this Privacy Policy.",
        "Legal Requirements: we may disclose your information if required to do so by law or in response to valid requests by public authorities.",
        "Advertisers and Publishers: for the purpose of facilitating advertising campaigns and reporting, aggregated or anonymized data may be shared with our advertising and publishing partners.",
      ]} />
    </Block>
    <Block title="4. Data Security">
      <p>We implement reasonable technical and organizational measures designed to protect your personal data from unauthorized access, use, alteration, and disclosure. However, no method of transmission over the Internet or method of electronic storage is 100% secure.</p>
    </Block>
    <Block title="5. Your Choices and Rights">
      <List items={[
        "Account Information: you may update or correct your information at any time by contacting us.",
        "Marketing Communications: you can opt out of receiving promotional emails from us by following the unsubscribe instructions in those emails.",
        "Interest-Based Advertising: you can opt out of tailored advertising at any time on our Opt Out page.",
        "Do Not Track: some web browsers may transmit \u201cdo not track\u201d signals. We do not currently respond to these signals.",
        "Data Subject Rights: depending on your jurisdiction, you may have rights regarding your personal data, such as the right to access, correct, delete, or restrict its processing. Please contact us to exercise these rights.",
      ]} />
    </Block>
    <Block title="6. Third-Party Links">
      <p>Our Services may contain links to third-party websites or services that are not owned or controlled by RresponsIQ. We are not responsible for the privacy practices or content of these third-party sites. We encourage you to review the privacy policies of any third-party sites you visit.</p>
    </Block>
    <Block title="7. Changes to This Privacy Policy">
      <p>We may update our Privacy Policy from time to time. You are advised to review this Privacy Policy periodically for any changes.</p>
    </Block>
    <Block title="8. Contact Us">
      <p>If you have any questions about this Privacy Policy, please contact us at <a href="mailto:business@responsiq.com" className="text-[#0E7A8F] underline hover:text-[#0B6273]">business@responsiq.com</a>.</p>
    </Block>
  </PageShell>
);

export const Terms = () => (
  <PageShell eyebrow="Legal · Terms" headline={<>Terms & <span className="accent-word">Conditions.</span></>} updated="March 27, 2026" testid="legal-terms">
    <p className="text-[#4A5A63]">
      Welcome to RresponsIQ. These Terms & Conditions ("Terms") govern your access to and use of the RresponsIQ website, platform, and services (collectively, the "Services"), operated by Xyle. By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Services.
    </p>
    <Block title="1. Acceptance of Terms">
      <p>By creating an account, accessing, or using the Services, you confirm that you have read, understood, and agree to be bound by these Terms, including any future modifications. These Terms constitute a legally binding agreement between you and RresponsIQ.</p>
    </Block>
    <Block title="2. Description of Services">
      <p>RresponsIQ provides a demand-side ad-tech platform designed for advertisers, agencies, and trading desks. Our Services include, but are not limited to, programmatic advertising, omnichannel campaign management, audience targeting, real-time bidding, analytics, and reporting tools.</p>
    </Block>
    <Block title="3. User Obligations">
      <List items={[
        "You must be at least 18 years old to use our Services.",
        "You agree to provide accurate, current, and complete information during registration and use of the Services.",
        "You are responsible for maintaining the confidentiality of your account credentials.",
        "You agree not to use the Services for any illegal or unauthorized purpose.",
        "Advertisers agree to comply with all applicable advertising laws and regulations.",
        "Campaigns must adhere to our supply policies and the content guidelines of our publisher and exchange partners.",
      ]} />
    </Block>
    <Block title="4. Intellectual Property">
      <p>All content, trademarks, service marks, trade names, logos, and intellectual property displayed on the RresponsIQ platform are the property of Xyle or its licensors. You may not use, reproduce, or distribute any content from our Services without explicit written permission.</p>
    </Block>
    <Block title="5. Disclaimers">
      <p>The Services are provided "as is" and "as available" without any warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement. RresponsIQ does not guarantee that the Services will be uninterrupted, error-free, or secure.</p>
    </Block>
    <Block title="6. Limitation of Liability">
      <p>To the fullest extent permitted by law, RresponsIQ and Xyle shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from (a) your access to or use of or inability to access or use the Services; (b) any conduct or content of any third party on the Services; or (c) unauthorized access, use, or alteration of your transmissions or content.</p>
    </Block>
    <Block title="7. Governing Law">
      <p>These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law principles.</p>
    </Block>
    <Block title="8. Changes to Terms">
      <p>RresponsIQ reserves the right to modify or revise these Terms at any time. We will notify you of any changes by posting the updated Terms on our website. Your continued use of the Services after any such changes constitutes your acceptance of the new Terms.</p>
    </Block>
    <Block title="9. Contact Us">
      <p>If you have any questions about these Terms, please contact us at <a href="mailto:business@responsiq.com" className="text-[#0E7A8F] underline hover:text-[#0B6273]">business@responsiq.com</a>.</p>
    </Block>
  </PageShell>
);

export const OptOut = () => {
  const [optedOut, setOptedOut] = useState(false);
  useEffect(() => {
    setOptedOut(document.cookie.includes("rresponsiq_optout=1"));
  }, []);
  const optOut = () => {
    document.cookie = "rresponsiq_optout=1; max-age=31536000; path=/; SameSite=Lax";
    setOptedOut(true);
  };
  return (
    <PageShell eyebrow="Privacy Choice" headline={<>Opt out of <span className="accent-word">interest-based ads.</span></>} testid="legal-opt-out">
      <p className="text-[#4A5A63]">
        RresponsIQ believes advertising can be relevant without being intrusive. If you would prefer not to receive ads tailored to your browsing patterns, you can opt out below. Your choice is respected across every publisher in our network.
      </p>
      <div className="card-brut p-6 sm:p-8">
        <h2 className="font-display font-bold text-xl tracking-tight text-[#101820]">Turn off tailored advertising</h2>
        <p className="mt-2 text-[#4A5A63]">
          Clicking the button will place a small, non-personal cookie on this browser. From that moment, RresponsIQ will stop using your anonymised browsing signals to select ads on any site in our network on this device.
        </p>
        {optedOut ? (
          <p className="mt-5 inline-flex items-center gap-2 font-nav font-semibold text-sm text-[#0E7A8F]" data-testid="opt-out-confirmation">
            <CheckCircle2 className="w-5 h-5" aria-hidden="true" /> You're opted out on this browser.
          </p>
        ) : (
          <button onClick={optOut} className="btn-primary mt-5" data-testid="opt-out-btn">Opt me out</button>
        )}
      </div>
      <Block title="What happens when you opt out">
        <p>A browser cookie is stored with the value <code className="font-mono2 text-xs bg-[#EDF3F5] px-1.5 py-0.5 rounded">rresponsiq_optout=1</code>. Our serving stack checks for this signal on every request from your device and, when present, skips interest-based selection. Ads still appear — they just are no longer personalised to your inferred preferences.</p>
        <List items={[
          "The opt-out is browser-specific. If you switch browsers or devices, please repeat the process on each one.",
          "Clearing your browser cookies will also clear your opt-out preference. Come back here and opt out again if that happens.",
          "The cookie contains no personal data — just a flag telling our servers to skip personalisation.",
        ]} />
      </Block>
      <Block title="Mobile apps">
        <p>RresponsIQ does not run interest-based advertising on mobile in-app inventory and does not collect device advertising identifiers for personalisation purposes. There is no separate opt-out required for mobile apps served through our SDK.</p>
      </Block>
      <Block title="Push notifications">
        <p>Push notifications are controlled by your browser or operating system, not by RresponsIQ directly. To stop notifications from a specific site:</p>
        <List items={[
          "Chrome / Edge / Brave: Settings → Privacy & Security → Site Settings → Notifications, then remove the site.",
          "Firefox: Settings → Privacy & Security → Permissions → Notifications → Settings, remove the site.",
          "Safari (macOS): Safari menu → Settings → Websites → Notifications, set the site to Deny or remove it.",
          "iOS / Android: use your device Settings → Notifications, then locate the browser or app and disable notifications.",
        ]} />
      </Block>
      <Block title="GDPR, CCPA and regional rights">
        <p>
          Residents of the EU, UK, California and other jurisdictions with relevant privacy laws have additional rights, including access, correction and deletion of personal data. See our <Link to="/privacy" className="text-[#0E7A8F] underline hover:text-[#0B6273]">Privacy Policy</Link> for full details, or email <a href="mailto:business@responsiq.com" className="text-[#0E7A8F] underline hover:text-[#0B6273]">business@responsiq.com</a> to exercise your rights.
        </p>
      </Block>
    </PageShell>
  );
};
